import { createRouter, createWebHistory } from 'vue-router';
import DefaultLayout from '@/layouts/default.vue';
import JobsLayout from '@/layouts/jobs.vue';
import Jobs from '@/views/jobs/jobs.vue';
import SettingsLayout from '@/layouts/settings.vue';
import Candidates from '@/views/candidates/index.vue';
import JobsCopilotActivation from '@/views/jobs/copilot-activation.vue';
import ConversationsTabConversationTab from '@/views/conversations/tabs/conversation-tab.vue';
import ConversationsTabApplicationsTab from '@/views/conversations/tabs/applications-tab.vue';
import ConversationsTabResumeTab from '@/views/conversations/tabs/resume-tab.vue';
import ConversationsTabNotesTab from '@/views/conversations/tabs/notes-tab.vue';
import MeService from '@/core/shared/me/me.service';
import { EmployerFeatureService } from '@/core/shared/employer-feature/employer-feature.service';
import SearchIndex from '@/views/search/index.vue';
import AiRecruitingTeam from '@/views/ai-recruiting-team/index.vue';
import FFHomepage from '@/views/FFHomepage.vue';
import JobsPage from '@/views/jobs/index.vue';
import Conversations from '@/views/conversations/conversations.vue';
import {
  CONNECT_CONSENT_ROUTE_NAME,
  PERFORMANCE_DASHBOARD_ROUTE_NAME,
} from '@/router/should-strip-query.util';
import { globalBeforeEach } from '@/router/global-before-each.guard';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'index',
      component: DefaultLayout,
      children: [
        { path: '/', redirect: '/jobs' },
        {
          path: '/home',
          name: 'ffHomepage',
          component: FFHomepage,
        },
        {
          path: '/candidates',
          name: 'candidates',
          component: Candidates,
        },
        {
          path: '/performance-dashboard',
          name: PERFORMANCE_DASHBOARD_ROUTE_NAME,
          component: () => import('@/views/performance-dashboard/v2/index.vue'),
          beforeEnter: (_to, _from, next) => {
            const meService = new MeService();
            if (meService.isEmployerAdmin) {
              next();
            } else {
              next({ path: '/jobs' });
            }
          },
        },
        {
          path: '/ai-recruiting-team',
          name: 'ai-recruiting-team',
          component: AiRecruitingTeam,
        },
        {
          path: '/',
          component: JobsLayout,
          children: [
            {
              path: '/sourcing',
              name: 'sourcing',
              children: [
                {
                  path: '',
                  name: 'sourcing-index',
                  redirect: '/search',
                },
                {
                  path: '/sourcing/job/:id',
                  name: 'sourcing-job-id',
                  redirect: '/search',
                },
              ],
            },
            {
              path: 'conversations',
              name: 'conversations',
              component: Conversations,
              redirect: (to) => ({
                path: '/jobs',
                params: to.params,
                query: to.query,
              }),
              children: [
                {
                  path: 'conversation',
                  name: 'conversation-tab',
                  component: ConversationsTabConversationTab,
                },
                {
                  path: 'applications',
                  name: 'applications-tab',
                  component: ConversationsTabApplicationsTab,
                },
                {
                  path: 'resume',
                  name: 'resume-tab',
                  component: ConversationsTabResumeTab,
                },
                {
                  path: 'notes',
                  name: 'notes-tab',
                  component: ConversationsTabNotesTab,
                },
              ],
            },
            {
              path: '/jobs',
              children: [
                {
                  path: '',
                  name: 'jobs-list',
                  component: JobsPage,
                },
                {
                  path: ':projectId',
                  name: 'job-detail',
                  redirect: (to) => `/jobs/${to.params.projectId}/candidates`,
                },
                {
                  path: ':projectId/candidates/:conversationId?',
                  name: 'job-candidates',
                  component: JobsPage,
                  redirect: (to) => ({
                    name: 'job-resume-tab',
                    params: to.params,
                    query: to.query,
                  }),
                  children: [
                    {
                      path: 'resume',
                      name: 'job-resume-tab',
                      component: ConversationsTabResumeTab,
                    },
                    {
                      path: 'conversation',
                      name: 'job-conversation-tab',
                      component: ConversationsTabConversationTab,
                    },
                    {
                      path: 'applications',
                      name: 'job-applications-tab',
                      component: ConversationsTabApplicationsTab,
                    },
                    {
                      path: 'notes',
                      name: 'job-notes-tab',
                      component: ConversationsTabNotesTab,
                    },
                    {
                      // Contact is a mobile-only screen rendered by MobileConversationsView.
                      // Desktop has no Contact tab and never links here; this route exists so
                      // the mobile detail tab bar can navigate to /contact. The resume pane is
                      // a harmless fallback for a manual desktop visit.
                      path: 'contact',
                      name: 'job-contact-tab',
                      component: ConversationsTabResumeTab,
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: '/settings',
          component: SettingsLayout,
          redirect: '/settings/company',
          children: [
            {
              path: 'company',
              name: 'settings-company',
              component: () => import('@/views/settings/company/company-overview.vue'),
            },
            {
              path: 'members',
              name: 'settings-members',
              component: () => import('@/views/settings/company/members/members.vue'),
            },
            {
              path: 'members/:id',
              name: 'settings-member-id',
              component: () => import('@/views/settings/company/members/member-profile.vue'),
              beforeEnter: (_to, _from, next) => {
                const meService = new MeService();
                if (meService.isEmployerAdmin) {
                  next();
                } else {
                  next({ name: 'settings-members' });
                }
              },
            },
            {
              path: 'permissions',
              name: 'settings-permissions',
              component: () => import('@/views/settings/company/permissions.vue'),
              beforeEnter: (_to, _from, next) => {
                const meService = new MeService();
                if (meService.isEmployerAdmin) {
                  next();
                } else {
                  next({ name: 'settings-company' });
                }
              },
            },
            {
              path: 'account-management',
              redirect: { name: 'settings-permissions' },
            },
            {
              path: 'profile',
              name: 'settings-profile',
              component: () => import('@/views/settings/account/account-profile.vue'),
            },
            {
              path: 'notifications',
              name: 'settings-notifications',
              component: () => import('@/views/settings/account/account-notifications.vue'),
            },
            {
              path: 'sign-in',
              name: 'settings-sign-in',
              component: () => import('@/views/settings/account/account-sign-in.vue'),
            },
            {
              path: 'ai-assistants',
              name: 'settings-ai-assistants',
              component: () => import('@/views/settings/account/account-ai-assistants.vue'),
              beforeEnter: (_to, _from, next) => {
                const employerFeatureService = new EmployerFeatureService();
                if (employerFeatureService.hasMcpConnectorEnabled) {
                  next();
                } else {
                  next({ name: 'settings-profile' });
                }
              },
            },
          ],
        },
        {
          path: '/search',
          name: 'search',
          component: SearchIndex,
        },
      ],
    },
    // Outside the employer shell: the user consents as themselves, across companies, before any employer
    // context exists.
    {
      path: '/connect/consent',
      name: CONNECT_CONSENT_ROUTE_NAME,
      component: () => import('@/views/connect/consent.vue'),
    },
    {
      path: '/jobs',
      name: 'jobs',
      component: Jobs,
      children: [
        {
          path: ':id/copilot-activation',
          name: 'jobs-copilot-activation',
          component: JobsCopilotActivation,
        },
      ],
    },
  ],
});

router.beforeEach(globalBeforeEach);

export default router;
